import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { cachedPriorAuthService, cachedPatientService } from '../services/cachedApiService';
import { PriorAuthorization, Patient } from '../types';
import Layout from '../components/Layout';
import '../styles/PatientList.css';
import { useAuth } from '../context/AuthContext';
import { useCache } from '../context/CacheContext';

const ProviderPAList: React.FC = () => {
  const [authorizations, setAuthorizations] = useState<PriorAuthorization[]>([]);
  const [filteredAuthorizations, setFilteredAuthorizations] = useState<PriorAuthorization[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [patients, setPatients] = useState<Record<string, Patient>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { user } = useAuth();
  const { invalidate } = useCache();
  const [selectedLetterId, setSelectedLetterId] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user || user.role !== 'provider') {
        setError('Only providers can access this page');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const assignedPids = await cachedPatientService.getAssignedPatients(user.username);
        const allPatients = await cachedPatientService.getAllPatients();
        const patientMap: Record<string, Patient> = {};
        allPatients
          .filter(patient => assignedPids.includes(patient.pid))
          .forEach(patient => { patientMap[patient.pid] = patient; });
        setPatients(patientMap);
        const allPAs = await cachedPriorAuthService.getAllPriorAuthorizations();
        const providerPAs = allPAs.filter(pa => assignedPids.includes(pa.patient));
        setAuthorizations(providerPAs);
        setFilteredAuthorizations(providerPAs);
        setLoading(false);
      } catch {
        setError('Failed to fetch prior authorizations');
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const handleDelete = async (id: number, patientName: string) => {
    if (!window.confirm(`Are you sure you want to delete the prior authorization for ${patientName}?`)) {
      return;
    }
    try {
      setError(null);
      setSuccessMessage(null);
      await cachedPriorAuthService.deletePriorAuth(id);
      
      // Invalidate related caches
      invalidate.priorAuths();
      const updatedAuthorizations = authorizations.filter(auth => auth.id !== id);
      setAuthorizations(updatedAuthorizations);
      setFilteredAuthorizations(updatedAuthorizations.filter(auth =>
        (patients[auth.patient]?.fname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patients[auth.patient]?.lname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        auth.request_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        auth.urgency.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (auth.clinical_reason && auth.clinical_reason.toLowerCase().includes(searchTerm.toLowerCase())))
      ));
      setSuccessMessage(`Prior authorization for ${patientName} has been deleted successfully.`);
    } catch {
      setError('Failed to delete prior authorization. Please try again.');
    }
  };

  // Modal for PDF base64 display (rendered using createPortal to document.body)
  const Modal = ({ authId, onClose }: { authId: number; onClose: () => void }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pdfData, setPdfData] = useState<string | null>(null);

    useEffect(() => {
      const fetchPdfData = async () => {
        try {
          setLoading(true);
          // Use absolute URL path to avoid relative path issues
          const response = await fetch(
            `/api/pa-letter-data/${authId}/`,
            { credentials: 'include', headers: { Accept: 'application/json' } }
          );
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch PDF data');
          }
          const data = await response.json();
          setPdfData(data.data);
          setLoading(false);
        } catch (err: any) {
          setError(err.message || 'Failed to load the letter. Please try again.');
          setLoading(false);
        }
      };
      fetchPdfData();
    }, [authId]);

    return ReactDOM.createPortal(
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3 style={{ fontSize: '1.2rem', margin: '0' }}>Prior Authorization Letter</h3>
            <button className="modal-close" onClick={onClose} aria-label="Close modal" style={{ padding: '5px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
          </div>
          <div className="pdf-container">
            {loading && (
              <div className="loading-container" style={{ padding: '30px 0' }}>
                <div className="loading-spinner" style={{ width: '40px', height: '40px', marginBottom: '15px' }}></div>
                <div className="loading-text" style={{ fontSize: '0.9rem' }}>Loading letter...</div>
              </div>
            )}
            {error && <div className="error">{error}</div>}
            {pdfData && !loading && !error && (
              <iframe
                src={`data:application/pdf;base64,${pdfData}`}
                title="PA Letter"
                width="100%"
                height="450px"
                style={{ border: '1px solid #edf2f7', borderRadius: '6px' }}
              />
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-primary" onClick={() => window.print()} style={{ padding: '6px 12px', fontSize: '12px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: '4px' }}>
                <path d="M6 9V2H18V9" stroke="currentColor" strokeWidth="2" />
                <path d="M6 18H4C3.47 18 2.96 17.79 2.59 17.41C2.21 17.04 2 16.53 2 16V11C2 10.47 2.21 9.96 2.59 9.59C2.96 9.21 3.47 9 4 9H20C20.53 9 21.04 9.21 21.41 9.59C21.79 9.96 22 10.47 22 11V16C22 16.53 21.79 17.04 21.41 17.41C21.04 17.79 20.53 18 20 18H18" stroke="currentColor" strokeWidth="2" />
                <path d="M18 14H6V22H18V14Z" stroke="currentColor" strokeWidth="2" />
              </svg>
              Print
            </button>
            <button className="btn btn-cancel" onClick={onClose} style={{ padding: '6px 12px', fontSize: '12px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: '4px' }}>
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" />
              </svg>
              Close
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading-container" style={{ padding: '50px 0' }}>
          <div className="loading-spinner" style={{ width: '50px', height: '50px', marginBottom: '20px' }}></div>
          <div className="loading-text" style={{ fontSize: '1rem' }}>Loading prior authorizations...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="alert alert-danger mb-3 fade-in" style={{ padding: '15px 20px' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '6px' }}>
            <path d="M12 9V13M12 17H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0378 2.66667 10.268 4L3.33978 16C2.56998 17.3333 3.53223 19 5.07183 19Z" stroke="currentColor" strokeWidth="2" />
          </svg>
          {error}
          <div className="mt-3">
            <button
              onClick={() => {
                invalidate.patients();
                invalidate.priorAuths();
                window.location.reload();
              }}
              className="btn btn-primary"
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <h2 className="mb-3 d-flex align-items-center justify-content-center gap-2">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M14 3V7C14 7.55 14.45 8 15 8H19" stroke="currentColor" strokeWidth="2" />
            <path d="M17 21H7C5.90 21 5 20.10 5 19V5C5 3.89 5.90 3 7 3H14L19 8V19C19 20.10 18.10 21 17 21Z" stroke="currentColor" strokeWidth="2" />
          </svg>
          Prior Authorizations
        </h2>

        {/* Search Bar */}
        <div className="search-container">
          <div className="search-input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="search-icon">
              <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search by name, type, urgency, or reason..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                if (value.trim() === '') {
                  setFilteredAuthorizations(authorizations);
                } else {
                  setFilteredAuthorizations(
                    authorizations.filter(auth =>
                      (patients[auth.patient]?.fname.toLowerCase().includes(value.toLowerCase()) ||
                      patients[auth.patient]?.lname.toLowerCase().includes(value.toLowerCase()) ||
                      auth.request_type.toLowerCase().includes(value.toLowerCase()) ||
                      auth.urgency.toLowerCase().includes(value.toLowerCase()) ||
                      (auth.clinical_reason && auth.clinical_reason.toLowerCase().includes(value.toLowerCase())))
                    )
                  );
                }
              }}
              className="search-input"
            />
            {searchTerm && (
              <button
                className="search-clear-btn"
                onClick={() => {
                  setSearchTerm('');
                  setFilteredAuthorizations(authorizations);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>
          <div className="search-results-count">
            {filteredAuthorizations.length} {filteredAuthorizations.length === 1 ? 'authorization' : 'authorizations'} found
          </div>
        </div>

        {successMessage && (
          <div className="alert alert-success mb-3 fade-in" style={{ padding: '15px 20px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '6px' }}>
              <path d="M22 11.0857V12.0057C21.9988 14.1621 21.3005 16.2604 20.0093 17.9875C18.7182 19.7147 16.9033 20.9782 14.8354 21.5896C12.7674 22.201 10.5573 22.1276 8.53447 21.3803C6.51168 20.633 4.78465 19.2518 3.61096 17.4428C2.43727 15.6338 1.87979 13.4938 2.02168 11.342C2.16356 9.19029 2.99721 7.14205 4.39828 5.5028C5.79935 3.86354 7.69279 2.72111 9.79619 2.24587C11.8996 1.77063 14.1003 1.98806 16.07 2.86572" stroke="currentColor" strokeWidth="2" />
              <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" />
            </svg>
            {successMessage}
          </div>
        )}

        <ul className="p-0" style={{ listStyle: 'none' }}>
          {filteredAuthorizations.length > 0 ? (
            filteredAuthorizations.map(auth => {
              const patient = patients[auth.patient];
              return (
                <li key={auth.id} className="patient-card fade-in">
                  <div className="d-flex align-items-center gap-2">
                    <div className="avatar">
                      {patient ? `${patient.fname[0]}${patient.lname[0]}` : "??"}
                    </div>
                    <div className="patient-name">
                      {patient ? (
                        <Link to={`/patients/${auth.patient}`}>
                          {patient.fname} {patient.lname}
                        </Link>
                      ) : (
                        <span>{auth.patient}</span>
                      )}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="status-badge" style={{ background: '#f4f4f4', color: '#555', padding: '4px 10px' }}>
                      <svg width="12" height="12" fill="none" style={{ marginRight: '4px', verticalAlign: 'middle' }}>
                        <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="2" />
                      </svg>
                      {auth.request_type}
                    </span>
                    <span className={`badge ${auth.urgency === 'urgent' ? 'warning' : 'normal'}`}>
                      {auth.urgency === 'urgent' ? (
                        <svg width="12" height="12" fill="none" style={{ marginRight: '3px' }}>
                          <path d="M12 9V13M12 17H12.01M5.1 19H18.9C20.5 19 21.4 17.33 20.7 16L13.7 4C13 2.67 11 2.67 10.3 4L3.3 16C2.6 17.33 3.5 19 5.1 19Z" stroke="currentColor" strokeWidth="2" />
                        </svg>
                      ) : (
                        <svg width="12" height="12" fill="none" style={{ marginRight: '3px' }}>
                          <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="2" />
                          <path d="M6 8V6M6 4H6.01" stroke="currentColor" strokeWidth="2" />
                        </svg>
                      )}
                      {auth.urgency}
                    </span>
                    <span className="status-badge" style={{ background: '#fffbe6', color: '#a87d00', padding: '4px 10px', fontSize: '11px' }}>
                      {new Date(auth.created_at).toLocaleDateString()}
                    </span>

                    {/* Action buttons */}
                    {auth.letter_file ? (
                      <button
                        onClick={() => setSelectedLetterId(auth.id)}
                        className="btn btn-view-letter btn-sm"
                        title="View Authorization Letter"
                      >
                        <svg width="14" height="14" fill="none" style={{ marginRight: '3px' }}>
                          <path d="M1 7S3 3 7 3s6 4 6 4-2 4-6 4S1 7 1 7Z" stroke="currentColor" strokeWidth="2" />
                          <circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="2" />
                        </svg>
                        Letter
                      </button>
                    ) : (
                      <span className="no-letter-badge">No Letter</span>
                    )}
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(auth.id, patient ? `${patient.fname} ${patient.lname}` : auth.patient)}
                      title="Delete"
                    >
                      <svg width="14" height="14" fill="none" style={{ marginRight: '3px' }}>
                        <path d="M10 4L9.5 10.5C9.5 11 9 11.5 8.5 11.5H4.5C4 11.5 3.5 11 3.5 10.5L3 4M5.5 6V9M7.5 6V9M8 4V2.5C8 2.25 7.75 2 7.5 2H5.5C5.25 2 5 2.25 5 2.5V4M2.5 4H10.5" stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                      Delete
                    </button>
                  </div>
                </li>
              );
            })
          ) : (
            <li className="empty">
              <svg width="36" height="36" fill="none" style={{ marginBottom: '12px', opacity: 0.5 }}>
                <path d="M14 3V7C14 7.55 14.45 8 15 8H19" stroke="currentColor" strokeWidth="2" />
                <path d="M17 21H7C5.90 21 5 20.10 5 19V5C5 3.89 5.90 3 7 3H14L19 8V19C19 20.10 18.10 21 17 21Z" stroke="currentColor" strokeWidth="2" />
              </svg>
              <div>
                {authorizations.length > 0
                  ? `No authorizations found matching "${searchTerm}"`
                  : "No prior authorizations found."}
              </div>
              {authorizations.length > 0 && searchTerm && (
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: '8px', padding: '6px 12px', fontSize: '12px' }}
                  onClick={() => {
                    setSearchTerm('');
                    setFilteredAuthorizations(authorizations);
                  }}
                >
                  Clear search
                </button>
              )}
            </li>
          )}
        </ul>

        {/* Render modal directly to document.body using createPortal */}
        {selectedLetterId && <Modal authId={selectedLetterId} onClose={() => setSelectedLetterId(null)} />}
      </div>
    </Layout>
  );
};

export default ProviderPAList;
